import {C8Store, CatalogInstanceChildStore, globalStores, LOG_GLOBAL_CATALOG_LOAD} from '@stores'
import {IPlanCollection} from '@stores/vlp'
import gql from 'graphql-tag';
import {computed, observable, action, runInAction, trace} from 'mobx'
import { guid } from '@utils';
import { computedFn } from '@api';

/// region Interfaces

export type IC8SeasonSelectorData = ICategory1C8 | ICategory2C8 | IPlanVersion | IMerchCollectionC8 | IMerchCollectionVersionC8 | ISeasonC8

export interface ICategory2CollectionC8 {
  id: string
  name: string
}

export interface IPlanVersion {
  id: string
  name: string
}
export interface ICategory2C8 {
  id: string
  name: string
  collections: Array<ICategory2CollectionC8>
  __typename: 'Category2'
}

export interface ICategory1C8 {
  id: string
  name: string
  category2s: Array<ICategory2C8>
}

export interface ISeasonC8 {
  id: string
  name: string
  category1s: Array<ICategory1C8>
}
export interface ICollectionVersionC8 {
  id: string
  name: string
  collectionId: string
  attributes?: Record<string, any>
}

export interface IMerchPlanC8 {
  id: string
  name: string
  exId?: string
  active?: boolean
  versions: Array<IPlanVersion>
  subCategories: Array<any>
  children: Array<IMerchFolderC8 | IMerchCollectionC8>
  type: 'MerchPlan'
}

export interface IMerchFolderC8 {
  id: string
  name: string
  children: Array<IMerchFolderC8 | IMerchCollectionC8>
  plan?: IMerchPlanC8
  type: 'MerchFolder'
}

export interface IMerchCollectionC8 {
  id: string
  name: string
  children: Array<IMerchFolderC8 | IMerchCollectionC8>

  folder?: IMerchFolderC8
  type: 'MerchCollection'
}

export interface IMerchCollectionVersionC8 {
  id: string,
  name: string,
  collectionId: string
}

export interface IMerchSeasonC8 {
  id: string
  planId: string
  name: string
}

/// endregion


export class C8ServicesStore {
  constructor(public c8: C8Store) {
    this.instance = new C8ServicesDataStore(this)
  }

  @observable.ref instance: C8ServicesDataStore

  @action reset = () => {
    this.instance = new C8ServicesDataStore(this)
  }

  @computed get c8ForCurrentWorkspace() : C8ServicesDataStore {
    return this.instance
  }

  @computed get currentDeploymentId() {
    const {catalog: {strings, stores: {deployments}}} = this
    return deployments.selectedDeploymentId ?? strings.emptyLabel
  }

  get catalog() { return this.c8.catalog}
  get filter() { return this.catalog.filter}
  get vlp() { return this.catalog.vlp}

  static c8DataByDeployment : {[deployment: string]: C8ServicesDataStore } = {}

  @action getDataFromC8 = async (field: string, graphqlServiceName: string, getCall: string, variables?, force?: boolean) => {
    if (graphqlServiceName) {
      // console.log(`${this.catalog.debugStoreIdPrefix} - Loading C8 hierarchy - ${field}`)

      const {instance} = this

      return instance.getDataFromC8(field, graphqlServiceName, getCall, variables, force)
    }
    else {
      console.debug(`!! Cannot get C8 data for ${field} - no graphqlServiceName info`)
    }
  }
}

export type C8Hierarchy = 'plan' | 'season' | 'merchSeason' | 'collection' | 'category1' | 'category2'

class C8ServicesDataStore extends CatalogInstanceChildStore{
  @action getDataFromC8 = async (field: string, graphqlServiceName: string, getCall: string, variables: any, force: boolean) => {
    // console.trace(this.catalog.debugStoreIdPrefix, `Loading C8 hierarchy - ${field} - ${this.deploymentId}`)
    const {loading} = this
    if (loading[field]) {
      // console.warn(`ALREADY Loading C8 hierarchy - ${field}`)
      return
    }

    if (!force && !_.isEmpty(this[field])) return this[field]

    loading[field] = true
    const {data} = await globalStores.apollo.client.query({
      query: gql`
                  ${getCall}
              `,
      ...(variables && {variables}),
    })

    // console.log({field, graphqlServiceName, getCall, data})
    runInAction(`Store result from C8 for (${field})`, () => {
      this[field] = data ? data[graphqlServiceName] : []
      loading[field] = false
      LOG_GLOBAL_CATALOG_LOAD && DEBUG && console.debug(`C8 hierarchy load - ${field} (${_.size(this[field])})`, this[field])
    })
  }
  @observable hasLoadedHierarchy = false

  constructor(public store: C8ServicesStore) {
    super(store.catalog)

    // console.log(`Construct C8 services store for deployment ${deploymentId}`)
  }

  @observable.ref plan: Array<IMerchPlanC8> = null
  @observable.ref season = []
  @observable.ref merchSeason: Array<IMerchSeasonC8> = []
  @observable.ref collection: Array<IMerchCollectionC8> = []
  @observable.ref category1 = []
  @observable.ref category2 = []
  @observable loading: { [key: string]: boolean } = {}
  @observable.ref collectionVersion: Array<IMerchCollectionVersionC8> = null

  @computed get collectionVersionEnhanced() {
    const {collectionVersion, vlp: {hierarchy: {collectionById}}} = this
    return (collectionVersion ?? []).map(cv => {
      const collection = (collectionById[cv.collectionId] ?? {}) as IPlanCollection
      return ({...cv, collection, plan: collection?.plan, subCategories: collection?.subCategories})
    })
  }
  @computed get collectionById() { return _.keyBy(this.collections, v => v.id)}

  get collectionsById() { return this.collectionById}


  getUniqId = (field) => _.uniq((this[`${field}ToShow`] || this[field] || []).map(({id}) => id))

  @action reset() {
    this.plan = null
    this.season = []
    this.merchSeason = []
    this.collection = []
    this.category1 = []
    this.category2 = []
  }

  @computed get planUniqueIds() {
    return this.getUniqId('plan')
  }

  @computed get seasonUniqueIds() {
    return this.getUniqId('season')
  }

  @computed get merchSeasonUniqueIds() {
    return this.getUniqId('merchSeason')
  }

  @computed get collectionUniqueIds() {
    return this.getUniqId('collection')
  }

  @computed get collectionVersionUniqueIds() {
    return this.getUniqId('collectionVersion')
  }

  @computed get category1UniqueIds() {
    return _.chain(this.season)
      .map(({category1s}) => category1s)
      .flattenDeep().uniqBy('id').map(({id}) => id).compact()
      .value()
  }

  @computed get category1s() {
    return _.chain(this.season)
      .map(({category1s, ...season}) => category1s.map(c1 => ({...c1, season})))
      .flatMap()
      .value()
  }

  @computed get seasonsDict() {
    return _.keyBy(this.season, c1 => c1.id)
  }

  @computed get category1sDict() {
    return _.keyBy(this.category1s, c1 => c1.id)
  }

  @computed get category1NameDict() {
    return _.groupBy(this.category1s, c1 => c1.name)
  }

  @computed get category2s() {
    return _.chain(this.season)
      .map(({category1s, ...season}) => category1s.map(({category2s, ...cat1}) => category2s.map(c2 => ({...c2, cat1, season}))))
      .flatMapDeep()
      .value()
  }

  @computed get category2sDict() {
    return _.keyBy(this.category2s, c1 => c1.id)
  }

  @computed get category2NameDict() {
    return _.groupBy(this.category2s, c1 => c1.name)
  }

  @computed get seasonNameDict() {
    return _.groupBy(this.season, s => s.name)
  }

  get seasonById() {
    return this.seasonsDict
  }

  get category1ById() {
    return this.category1sDict
  }

  get category2ById() {
    return this.category2sDict
  }

  @computed get collections() : Array<ICategory2CollectionC8 & {cat2: ICategory2C8, cat1: ICategory1C8, season: ISeasonC8}> {
    return _.chain(this.category2s).map(({collections, ...cat2}) => collections.map(c => ({...c, cat2: cat2, cat1: cat2.cat1, season: cat2.season})))
      .flatMap()
      .value()
  }

  @computed get collectionNameDict() {
    return _.groupBy(this.collections, c => c.name)
  }

  @computed get category2UniqueIds() {
    return _.chain(this.season)
      .map(({category1s}) => category1s.map(({category2s}) => category2s))
      .flattenDeep().uniqBy('id').map(({id}) => id).compact()
      .value()
  }

  category1Data = (seasonSelectedIds) => {
    const filterCondition = ({id}) => seasonSelectedIds.length ? seasonSelectedIds.includes(id) : seasonSelectedIds
    const categoryData = _.chain(this.season)
      .filter(filterCondition)
      .map(({category1s}) => category1s)
      .flattenDeep().uniqBy('id').compact()
      .value()
    this.category1 = categoryData
    return categoryData
  }

  category2Data = (category1SelectedIds) => {
    const category1DataIds = this.category1.map(({id}) => id)
    const filterCondition = ({id}) => category1SelectedIds.length ? category1SelectedIds.includes(id) : category1DataIds.includes(id)
    const categoryData = _.chain(this.season)
      .map(({category1s}) => category1s.filter(filterCondition).map(({category2s}) => category2s))
      .flattenDeep().uniqBy('id').compact()
      .value()
    this.category2 = categoryData
    return categoryData
  }

  collectionData = (category2SelectedIds) => {
    const category2DataIds = this.category2.map(({id}) => id)
    const filterCondition = ({id}) => category2SelectedIds.length ? category2SelectedIds.includes(id) : category2DataIds.includes(id)
    const collectionData = _.chain(this.season)
      .map(({category1s}) => category1s.map((({category2s}) => category2s.filter(filterCondition).map(({collections}) => collections))))
      .flattenDeep().uniqBy('id').compact()
      .value()
    this.collection = collectionData
    return collectionData
  }

  @computed get seasonSelectedIds() {
    const {filter: {state: {groups: {season}}}} = this,
      seasonSelectedIds = (Array.from(season?.selected ?? [])).map(([key, value]) => season.groupedByValue[key]?.id).flat()

    return seasonSelectedIds.filter(_.identity)
  }

  @computed get seasonToShow() {
    const {seasonSelectedIds} = this,
      filterCondition = ({id}) => seasonSelectedIds.length ? seasonSelectedIds.includes(id) : true,
      seasons = _.chain(this.season)
        .filter(filterCondition)
        .flattenDeep().uniqBy('id').compact()
        .value()
    return seasons
  }

  @computed get category1ToShow() {
    const {seasonToShow} = this
    const categoryData = _.chain(seasonToShow)
      .map(({category1s}) => category1s)
      .flattenDeep().uniqBy('id').compact()
      .value()
    return categoryData
  }

  @computed get category2ToShow() {
    const {category1ToShow, filter: {state: {groups: {category1}}}} = this,
      category1SelectedIds = (Array.from(category1?.selected ?? [])).map(([key, value]) => category1.groupedByValue[key]?.id).flat()
    const filterCondition = ({id}) => category1SelectedIds.length ? category1SelectedIds.includes(id) : true
    const categoryData = _.chain(category1ToShow)
      .filter(filterCondition)
      .map(({category2s}) => category2s)
      .flattenDeep().uniqBy('id').compact()
      .value()
    return categoryData
  }

  get filter() { return this.catalog.filter}

  @computed get collectionToShow() {
    const {filter: {state: {groups: {category2}}}, category2ToShow} = this

    const collectionData = _.chain(this.category2ToShow)
      .map(({collections}) => collections)
      .flattenDeep().uniqBy('id').compact()
      .value()
    return collectionData
  }

  @computed get collectionVersionByCollectionId() {
    return _.groupBy(this.collectionVersion ?? [], cv => cv?.collectionId)
  }

  @computed get collectionVersionById() {
    const {collectionVersionEnhanced, vlp: {hierarchy: {collectionById}}} = this
    return _.keyBy(collectionVersionEnhanced, cv => cv?.id)
  }

  @action getCollectionVersionByCollection = (collectionIds: Array<string>) => {
    const filteredCollectionVersion = []
    collectionIds.map(cId => (this.collectionVersion ?? []).filter((cVersion) => cVersion?.collectionId === cId)).filter(_.identity)

    return _.uniqBy(_.flatMap(filteredCollectionVersion), cv => cv.id)
  }

  @action getSubCategoriesByPlan = (planId: string) => {
    const subCategories = _.first(this.c8PlanHierarchy.filter(p => p.id === planId))?.subCategories?.filter(subCat => _.size(subCat?.collections) > 0)
    return subCategories
  }

  @computed get collectionVersionToShow() {
    const {catalog: {seasonSelector: {allAvailableCollections}}} = this,
      {collectionVersionByCollectionId, collectionVersion} = this

    if (!_.size(this.c8PlanHierarchy)) return collectionVersion
    return _.uniq(_.flatMap(allAvailableCollections.map(c => collectionVersionByCollectionId[c.id])).filter(_.identity))
  }

  @action buildSubCategories = (subCategory, planData, level, path, parent, merchFolderLevel, collectionSubCategories, collectionVersions, planVersions = []) => {
    let parents: Array<string> = path ? path.split('.') : [],
      subCategoryCreateData = {
        id: subCategory && subCategory.id || guid(),
        exId: subCategory && subCategory.id || null,
        name: subCategory && subCategory.name || '',
        path,
        level,
        plan: {
          _id: planData.planId,
          name: planData.planName,
          exId: planData.planId,
        },
        collections: [],
        versions: planVersions
      }

    if (parents && parents.length) {
      subCategoryCreateData['parent'] = {
        id: parents[parents.length - 1]
      }
    }
    if ((subCategory && subCategory.children && !_.isEmpty(subCategory.children) && subCategory.children[0].type && subCategory.children[0].type === 'MerchCollection')
        || merchFolderLevel === level) {
      subCategoryCreateData['leaf'] = true
    } else {
      subCategoryCreateData['leaf'] = false
    }
    // Connect children to its parent
    if (parent) {
      subCategoryCreateData["parent"] = {
        id: parent.id,
        exId: parent.exId,
        name: parent.name
      }
    }

    if (collectionSubCategories) {
      collectionSubCategories.push({id: subCategory.id, exId: subCategory.id, name: subCategory.name})
    } else {
      collectionSubCategories = []
      collectionSubCategories.push({id: subCategory.id, exId: subCategory.id, name: subCategory.name})
    }

    const nextLevel = level + 1
    path = path === null ? subCategoryCreateData.id : path + `.${subCategoryCreateData.id}`
    if ((subCategory && subCategory.children && !_.isEmpty(subCategory.children) && subCategory.children[0].type && subCategory.children[0].type === 'MerchCollection')
        || merchFolderLevel === level) {
      let collections: any = {}
      _.map(merchFolderLevel === level ? subCategory.collections ? subCategory.collections : collections.items : subCategory.children, (collection) => {
        const exIds = path.split("."),
          finalCats = []
        _.map(collectionSubCategories, (cat) => {
          if (_.some(exIds, (c) => c === cat.id)) {
            finalCats.push(cat)
          }
          return
        })
        let collectionInputData = {
          name: collection.name,
          exId: collection.id,
          id: collection.id,
          subCategories: finalCats,
          plan: {
            id: planData.planId,
            name: planData.planName,
            exId: planData.planId,
          },
          attributes: {},
          versions: ((collectionVersions ?? {})[collection.id] ?? []).map(v => ({...v, exId: v.id}))
        }
        subCategoryCreateData.collections.push(collectionInputData)
        collectionSubCategories = null
      })
      return [subCategoryCreateData]
    }

    let categories = []
    if (subCategory && subCategory.children) {
      categories = _.map(subCategory && subCategory.children, (child) => {
        return this.buildSubCategories(child, planData, nextLevel, path, subCategoryCreateData, merchFolderLevel, collectionSubCategories, collectionVersions, planVersions)
      })
    }
    return [].concat([subCategoryCreateData]).concat(_.flatten(categories))
  }

  @computed get c8PlanVersionsName() {
    return _.chain(this.c8PlanHierarchy).map(p => p?.versions?.map((v: IPlanVersion) => v?.name)).flatten().uniq().value()
  }

  @computed get c8PlanHierarchy() {
    if (!this.vlp) {
      console.log(`No vlp`)
      return null
    }

    const {plan, loading, buildSubCategories} = this

    if (loading.plan || !plan) return null

    // trace()

    const {vlp: {hierarchy: {merchFolderLevel}}, collectionVersionByCollectionId} = this,
      plans = _.cloneDeep(plan),
      c8Plans: Array<IMerchPlanC8> = plans.reduce((acc, p: IMerchPlanC8) => {
        const planVersions = p.versions?.map(v => ({...v, exId: v.id}))
        p.exId = p.id
        p.subCategories = p.children?.reduce((acc, subCategory) => (
          [
            ...acc,
            ...buildSubCategories(subCategory, {planName: p.name, planId: p.id, allSubcategories: null}, 1, null, null, merchFolderLevel, null, collectionVersionByCollectionId, planVersions)
          ]), [])

        return [...acc, p]
      }, [])

    return _.size(c8Plans) ? c8Plans : []
  }
}


// WEBPACK FOOTER //
// ./src/stores/c8/C8ServicesStore.ts